<template>
  <component
    v-bind="combinedAttributes"
    :is="tag"
    :class="classes"
    :disabled="isButton && (disabled || isLoading)"
    @click="onClick"
  >
    <template v-if="isLoading">
      {{ loadingText }}
      <span class="spinner-border spinner-border-sm ml-2" />
    </template>

    <template v-else>
      <CxIcon v-if="icon && iconPosition === BUTTON_ICON_POSITIONS.left" :icon="icon" :class="iconClasses" />

      <slot />

      <CxIcon
        v-if="icon && iconPosition === BUTTON_ICON_POSITIONS.right"
        :icon="icon"
        class="ml-1"
        :class="iconClasses"
      />
    </template>
  </component>
</template>

<script setup lang="ts">
import {
  BUTTON_ICON_POSITIONS,
  BUTTON_SIZES,
  BUTTON_TOOLTIP_POSITIONS,
  BUTTON_TYPES,
  BUTTON_VARIANTS,
  PRIMARY_COLOR,
} from "@/designSystem/Button/constant";
import { computed, defineEmits, onUnmounted, PropType, useSlots, watch } from "vue";
import { CxIcon } from "@/designSystem";
import jQuery from "jquery";
import type { ButtonType, IconType, SizeType, TooltipPositionType, VariantType } from "@/designSystem/Button/constant";
import useTranslate from "@/core/composables/translate";

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },

  icon: {
    type: String,
    required: false,
  },

  iconPosition: {
    type: String as PropType<IconType>,
    default: BUTTON_ICON_POSITIONS.left,
    validator: (value: string): value is IconType => value in BUTTON_ICON_POSITIONS,
  },

  isLoading: {
    type: Boolean,
    default: false,
  },

  size: {
    type: String as PropType<SizeType>,
    default: BUTTON_SIZES.md,
    validator: (value: string): value is SizeType => value in BUTTON_SIZES,
  },

  to: {
    type: String,
    required: false,
    default: null,
  },

  type: {
    type: String as PropType<ButtonType>,
    default: BUTTON_TYPES.button,
    validator: (value: string): value is ButtonType => value in BUTTON_TYPES,
  },

  variant: {
    type: String as PropType<VariantType>,
    default: BUTTON_VARIANTS.primary,
    validator: (value: string): value is VariantType => value in BUTTON_VARIANTS,
  },

  tooltip: {
    type: String as PropType<string | null>,
    default: null,
  },

  tooltipPlacement: {
    type: String as PropType<TooltipPositionType>,
    default: "top",
    validator: (value: string): value is TooltipPositionType => value in BUTTON_TOOLTIP_POSITIONS,
  },

  customColor: {
    type: String as PropType<string>,
    required: false,
    default: PRIMARY_COLOR,
  },
});

const emit = defineEmits<{
  (event: "click", value: MouseEvent): void;
}>();

const { translate } = useTranslate();

const onClick = (event: Readonly<MouseEvent>): void => {
  if (props.disabled) {
    event.preventDefault();
    event.stopPropagation();
    return;
  }
  emit("click", event);
};

const classes = computed(() => [
  "btn",
  `btn-${props.variant}`,
  `btn-${props.size}`,
  {
    disabled: props.disabled,
  },
]);

const slots = useSlots();
const hasDefaultSlotContent = !!slots.default && slots.default().length > 1;

const iconClasses = computed(() =>
  props.iconPosition === BUTTON_ICON_POSITIONS.left && hasDefaultSlotContent ? "mr-1" : ""
);

const loadingText = computed(() => translate("SHARED.SIMPLE_LOADING"));

const isRouterLink = computed(() => !!props.to);

const isCloseButton = computed(() => props.variant === BUTTON_VARIANTS.close);

const tag = computed(() => {
  if (isCloseButton.value) {
    return "button";
  }
  return isRouterLink.value ? "router-link" : "button";
});

const isButton = computed(() => tag.value === "button");

const tooltipAttributes = computed(() => {
  return {
    "data-placement": props.tooltipPlacement,
    "data-toggle": "tooltip",
    "data-original-title": props.tooltip,
  };
});

const combinedAttributes = computed(() => {
  return {
    ...(isRouterLink.value ? { to: { name: props.to } } : { type: props.type }),
    ...(isCloseButton.value ? { "aria-label": "Close" } : {}),
    ...tooltipAttributes.value,
  };
});

watch(
  () => props.tooltip,
  (newTooltip) => {
    const buttonTooltipElement = document.querySelector(`[data-toggle="tooltip"]`);
    if (buttonTooltipElement) {
      // @ts-ignore
      jQuery(buttonTooltipElement).tooltip("dispose");
      buttonTooltipElement.setAttribute("data-original-title", newTooltip || "");
      // @ts-ignore
      jQuery(buttonTooltipElement).tooltip();
    }
  },
  { immediate: true }
);

onUnmounted(() => {
  const buttonTooltipElement = document.querySelector(`[data-toggle="tooltip"]`);

  if (buttonTooltipElement) {
    // @ts-ignore
    jQuery(buttonTooltipElement).tooltip("dispose");
  }
});
</script>

<style lang="scss" scoped>
@import "@/designSystem/style/main.scss";
@import "bootstrap/scss/mixins/breakpoints";
@import "@/designSystem/style/variables/_colors.scss";
@import "@/designSystem/style/variables/_button.scss";
@import "@/designSystem/style/variables/_font.scss";

@mixin button-variant($color, $background) {
  color: $color !important;
  background-color: $background !important;
  border: none !important;

  &:hover:not(.disabled, :disabled, .btn-link) {
    color: $color !important;
    background-color: $background !important;
    opacity: 0.8;
    border: none !important;
  }

  &.disabled,
  &:disabled {
    cursor: not-allowed;
    pointer-events: all !important;
  }

  i,
  svg,
  span {
    color: $color !important;
  }
}

@mixin button-variant-outline($color) {
  color: $color !important;
  background-color: transparent !important;
  border-color: $color !important;

  &:hover:not(.disabled, &:disabled) {
    color: $white !important;
    background-color: $color !important;

    i,
    svg,
    span {
      color: white !important;
    }
  }

  &.disabled,
  &:disabled {
    cursor: not-allowed;
    pointer-events: all !important;
  }

  i,
  svg,
  span {
    color: $color !important;
  }
}

@mixin button-size($height, $font-size) {
  height: $height !important;
  font-size: $font-size !important;
}

.design-system:not(.legacy-design) {
  .btn {
    display: flex !important;
    align-items: center;
    justify-content: center;
    height: $btn-height-md !important;
    font-family: $font-family-title;
    transition: unset !important;

    @include media-breakpoint-between(xs, lg) {
      height: unset !important;
    }

    &-sm {
      @include button-size($btn-height-sm, $btn-font-size-sm);
    }

    &-md {
      @include button-size($btn-height-md, $btn-font-size-md);
    }

    &-lg {
      @include button-size($btn-height-lg, $btn-font-size-lg);
    }

    &-primary {
      @include button-variant($white, $primary);
    }

    &-secondary {
      @include button-variant($white, $secondary);
    }

    &-danger {
      @include button-variant($white, $danger);
    }

    &-light {
      @include button-variant($white, $gray-600);
    }

    &-custom {
      @include button-variant($white, v-bind("props.customColor"));
    }

    &-link {
      @include button-variant($secondary, $white);
      text-decoration: none !important;
      background-color: transparent !important;
      border: none !important;

      &:hover {
        background-color: transparent !important;
        border: none !important;
        color: rgba(#3699ff, 0.8) !important;

        i,
        svg,
        span {
          color: rgba(#3699ff, 0.8) !important;
        }
      }
    }

    &-outline-primary {
      @include button-variant-outline($primary);
    }

    &-outline-secondary {
      @include button-variant-outline($secondary);
    }

    &-outline-danger {
      @include button-variant-outline($danger);
    }

    &-outline-light {
      @include button-variant-outline($gray-600);
    }

    &-outline-custom {
      @include button-variant-outline(v-bind("props.customColor"));
    }

    &-table-action {
      padding: 0;
    }

    .text-link {
      color: $secondary !important;
    }

    .text-light {
      color: $gray-600 !important;
    }
  }
}
</style>
